<template>
  <div class="today-recommendation" ref="container">
    <!-- 好文推荐部分 -->
    <div class="recommendation-section" :style="{ height: recommendationHeight + 'px' }">
      <div class="section-header">
        <span class="header-icon">⭐</span>
        <h2 class="section-title">好文推荐</h2>
        <span class="header-date">{{ currentDate }}</span>
      </div>
      
      <div class="recommendation-list" v-loading="loading" @scroll="handleScroll">
        <div 
          v-for="item in recommendationList" 
          :key="item.id" 
          class="recommendation-item"
          @click="openArticle(item)"
        >
          <div class="item-content">
            <div class="item-header">
              <span class="item-text">{{ item.author }}</span>
              <span class="item-separator"> | </span>
              <span class="item-text">{{ item.title }}</span>
            </div>
            <div class="item-keywords" v-if="item.book_keywords_list && item.book_keywords_list.length > 0">
              <span 
                v-for="keyword in item.book_keywords_list" 
                :key="keyword.book_keywords_id"
                class="keyword-tag"
              >
                {{ keyword.keywords }}
              </span>
            </div>
          </div>
        </div>
        
        <!-- 加载更多 -->
        <div v-if="loadingMore" class="loading-more">
          <div class="loading-spinner"></div>
          <span>加载中...</span>
        </div>
        
        <!-- 没有更多数据 -->
        <div v-if="!hasMore && recommendationList.length > 0" class="no-more-data">
          已加载全部内容
        </div>
        
        <!-- 空状态 -->
        <div v-if="!loading && !loadingMore && recommendationList.length === 0" class="empty-state">
          <div class="empty-text">暂无好文推荐</div>
          <button class="retry-btn" @click="loadGoodArticleList">重新加载</button>
        </div>
      </div>
    </div>

    <!-- 学科分类部分 -->
    <div class="category-section" :style="{ height: categoryHeight + 'px' }">
      <div 
        class="section-header"
        @mousedown="startResize"
      >
        <!-- <span class="header-icon">📚</span> -->
        <h2 class="section-title">学科分类</h2>
      </div>
      
      <div class="search-box">
        <input 
          type="text" 
          v-model="searchKeyword"
          placeholder="搜索学科..."
          class="search-input"
          @input="handleSearch"
        />
      </div>
      
      <div class="category-list" v-loading="categoriesLoading">
        <div 
          v-for="category in filteredCategories" 
          :key="category.major_id"
          class="category-wrapper"
        >
          <div 
            class="category-item"
            :class="{ 'is-expanded': category.isExpanded }"
            @click="toggleCategory(category)"
          >
            <div class="category-name">{{ category.major_name }}</div>
            <div class="category-arrow" :class="{ 'is-expanded': category.isExpanded }">
              <span>▼</span>
            </div>
          </div>
          
          <!-- 公众号下拉列表 -->
          <transition name="slide-down">
            <div v-if="category.isExpanded" class="account-dropdown">
              <div v-if="category.accountListLoading" class="account-loading">
                加载中...
              </div>
              <div v-else-if="category.accountList && category.accountList.length > 0" class="account-list">
                <div 
                  v-for="account in category.accountList" 
                  :key="account.id"
                  class="account-item"
                >
                  <div class="account-info">
                    <div class="account-name">{{ account.name }}</div>
                    <div class="account-meta" v-if="account.category && account.category.length > 0">
                      {{ account.category.map(c => c.major_name).join('、') }}
                    </div>
                  </div>
                  <div class="account-rating" v-if="account.score">
                    <div class="stars">
                      <span 
                        v-for="n in 5" 
                        :key="n" 
                        class="star"
                        :class="{ filled: n <= Math.floor(account.score.total_average_star) }"
                      >★</span>
                    </div>
                    <span class="score-value" v-if="account.score.total_average_score">
                      {{ account.score.total_average_score }}
                    </span>
                  </div>
                </div>
              </div>
              <div v-else class="account-empty">
                暂无公众号
              </div>
            </div>
          </transition>
        </div>
        
        <!-- 空状态 -->
        <div v-if="!categoriesLoading && filteredCategories.length === 0" class="empty-categories">
          <div class="empty-text">暂无学科分类</div>
          <button class="retry-btn" @click="loadMajorList">重新加载</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { getGoodArticleList, getOfficialAccountList } from '@/http/api/news'
import { getMajorList } from '@/http/api/category'

export default {
  name: 'TodayRecommendation',
  data() {
    return {
      // 当前日期
      currentDate: '11月7日',
      // 推荐内容列表
      recommendationList: [],
      // 学科分类列表（从接口获取）
      categories: [],
      // 学科分类加载状态
      categoriesLoading: false,
      searchKeyword: '',
      loading: false,
      loadingMore: false,
      page: 1,
      limit: 10,
      hasMore: true,
      // 公众号列表相关
      officialAccountList: [],
      accountListLoading: false,
      selectedCategory: null,
      // 存储每个分类的展开状态和公众号列表
      categoryAccountMap: {},
      // 拖拽相关
      isResizing: false,
      startY: 0,
      startRecommendationHeight: 0,
      recommendationHeight: 0,
      categoryHeight: 0,
      containerHeight: 0,
      // 滚动防抖定时器
      scrollTimer: null
    }
  },
  computed: {
    // 过滤后的分类列表
    filteredCategories() {
      if (!this.searchKeyword) {
        return this.categories
      }
      return this.categories.filter(category => 
        category.major_name.toLowerCase().includes(this.searchKeyword.toLowerCase())
      )
    }
  },
  mounted() {
    // 初始化当前日期
    this.updateCurrentDate()
    // 加载好文推荐列表
    this.loadGoodArticleList()
    // 加载学科分类列表
    this.loadMajorList()
    // 初始化高度
    this.$nextTick(() => {
      this.initializeHeights()
    })
    // 添加窗口大小变化监听
    window.addEventListener('resize', this.handleWindowResize)
  },
  beforeDestroy() {
    // 移除事件监听
    window.removeEventListener('resize', this.handleWindowResize)
    document.removeEventListener('mousemove', this.handleResize)
    document.removeEventListener('mouseup', this.stopResize)
    // 清理滚动定时器
    if (this.scrollTimer) {
      clearTimeout(this.scrollTimer)
    }
  },
  methods: {
    // 更新当前日期
    updateCurrentDate() {
      const now = new Date()
      const month = now.getMonth() + 1
      const day = now.getDate()
      this.currentDate = `${month}月${day}日`
    },
    
    // 加载好文推荐列表
    async loadGoodArticleList(isLoadMore = false) {
      if (isLoadMore) {
        this.loadingMore = true
      } else {
        this.loading = true
        this.page = 1
        this.hasMore = true
      }
      
      try {
        const result = await getGoodArticleList({
          page: this.page,
          limit: this.limit,
          search: ''
        })
        
        if (result && Array.isArray(result)) {
          const newArticles = result
          
          if (isLoadMore) {
            this.recommendationList = [...this.recommendationList, ...newArticles]
          } else {
            this.recommendationList = newArticles
          }
          
          // 判断是否还有更多数据
          this.hasMore = newArticles.length === this.limit
          this.page++
        }
      } catch (error) {
        console.error('加载好文推荐出错:', error)
        this.$message.error('加载失败')
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },
    
    // 滚动事件处理（带防抖）
    handleScroll(e) {
      // 清除之前的定时器
      if (this.scrollTimer) {
        clearTimeout(this.scrollTimer)
      }
      
      // 设置新的定时器，防抖处理
      this.scrollTimer = setTimeout(() => {
        const { scrollTop, scrollHeight, clientHeight } = e.target
        // 当滚动到距离底部20px以内时触发加载
        const threshold = 20
        
        if (scrollTop + clientHeight >= scrollHeight - threshold && 
            this.hasMore && 
            !this.loadingMore && 
            !this.loading) {
          console.log('触发懒加载，当前页:', this.page)
          this.loadGoodArticleList(true)
        }
      }, 100) // 100ms防抖
    },
    
    // 打开文章链接
    openArticle(item) {
      if (item.url) {
        window.open(item.url, '_blank')
      } else {
        this.$message.warning('该文章暂无链接')
      }
    },
    
    // 切换分类展开/收起
    async toggleCategory(category) {
      // 切换展开状态（使用$set确保响应式）
      this.$set(category, 'isExpanded', !category.isExpanded)
      
      // 如果展开且还没有加载过公众号列表，则加载
      if (category.isExpanded && !category.accountList) {
        await this.loadOfficialAccountList(category)
      }
    },
    
    // 加载公众号列表
    async loadOfficialAccountList(category) {
      // 设置该分类的加载状态
      this.$set(category, 'accountListLoading', true)
      
      try {
        const params = {
          page: 1,
          limit: 1000
        }
        
        // 根据学科分类ID传递参数
        // 如果parent_id为0、null或undefined，说明是一级分类，使用label_id
        // 否则是二级分类，使用category_id
        if (category && 
            (category.parent_id === 0 || 
             category.parent_id === null || 
             category.parent_id === undefined)) {
          params.label_id = category.major_id
        } else {
          params.category_id = category.major_id
        }
        
        const response = await getOfficialAccountList(params)
        
        if (response && response.code === 200 && response.result) {
          // 将公众号列表存储到分类对象中
          this.$set(category, 'accountList', response.result)
          console.log(`获取到 ${category.major_name} 分类下 ${response.result.length} 个公众号`)
        } else {
          this.$set(category, 'accountList', [])
          if (response && response.message) {
            this.$message.warning(`加载失败: ${response.message}`)
          } else {
            this.$message.warning('获取公众号列表失败，请稍后重试')
          }
        }
      } catch (error) {
        console.error('加载公众号列表出错:', error)
        this.$set(category, 'accountList', [])
        this.$message.error('网络错误，请检查网络连接后重试')
      } finally {
        this.$set(category, 'accountListLoading', false)
      }
    },
    
    // 处理搜索
    handleSearch() {
      console.log('搜索关键词:', this.searchKeyword)
    },
    
    // 获取图片URL
    getImageUrl(imagePath) {
      if (!imagePath) return ''
      
      // 如果已经是完整的URL，直接返回
      if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
        return imagePath
      }
      
      // 处理相对路径，使用OSS基础URL
      // 确保路径以 / 开头
      const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`
      
      // 使用书客网的OSS基础URL
      const ossBaseUrl = 'https://oss.bookor.com.cn'
      
      // 如果路径不是以 /uploads 开头，添加它
      const finalPath = cleanPath.startsWith('/uploads') ? cleanPath : `/uploads${cleanPath}`
      
      const fullUrl = `${ossBaseUrl}${finalPath}`
      
      return fullUrl
    },
    
    // 处理图片加载错误
    handleImageError(event) {
      console.log('图片加载失败:', event.target.src)
      // 隐藏失败的图片，显示默认头像
      event.target.style.display = 'none'
      // 创建或显示默认头像
      const avatarContainer = event.target.parentElement
      if (!avatarContainer.querySelector('.default-avatar')) {
        const accountName = event.target.alt || '?'
        const defaultAvatar = document.createElement('div')
        defaultAvatar.className = 'default-avatar'
        defaultAvatar.textContent = accountName.charAt(0)
        avatarContainer.appendChild(defaultAvatar)
      }
    },

    // 加载学科分类列表
    async loadMajorList() {
      this.categoriesLoading = true
      try {
        const response = await getMajorList()
        
        // 处理接口返回的数据结构
        let majorList = []
        if (Array.isArray(response)) {
          majorList = response
        } else if (response && response.result && Array.isArray(response.result)) {
          majorList = response.result
        }
        
        // 只显示一级分类，不显示children下的二级分类
        const categories = majorList.map(major => ({
          major_id: major.major_id,
          major_name: major.major_name,
          parent_id: major.parent_id,
          status: major.status,
          level: 1, // 标记为一级分类
          isExpanded: false, // 展开状态
          accountList: null, // 公众号列表
          accountListLoading: false // 加载状态
        }))
        
        this.categories = categories
        
      } catch (error) {
        console.error('加载学科分类失败:', error)
        this.$message.error('加载学科分类失败')
        // 如果接口失败，使用默认分类
        this.categories = [
          { 
            major_id: 0, 
            major_name: '全部', 
            parent_id: 0, 
            level: 0, 
            isExpanded: false,
            accountList: null,
            accountListLoading: false
          }
        ]
      } finally {
        this.categoriesLoading = false
      }
    },

    // 初始化高度
    initializeHeights() {
      if (this.$refs.container) {
        this.containerHeight = this.$refs.container.clientHeight
        // 默认好文推荐占60%，学科分类占40%
        this.recommendationHeight = Math.floor(this.containerHeight * 0.6)
        this.categoryHeight = this.containerHeight - this.recommendationHeight
      }
    },

    // 窗口大小变化处理
    handleWindowResize() {
      if (this.$refs.container) {
        const newContainerHeight = this.$refs.container.clientHeight
        // 按比例调整
        const ratio = this.recommendationHeight / this.containerHeight
        this.containerHeight = newContainerHeight
        this.recommendationHeight = Math.floor(newContainerHeight * ratio)
        this.categoryHeight = newContainerHeight - this.recommendationHeight
      }
    },

    // 开始拖拽
    startResize(e) {
      this.isResizing = true
      this.startY = e.clientY
      this.startRecommendationHeight = this.recommendationHeight
      
      // 添加全局事件监听
      document.addEventListener('mousemove', this.handleResize)
      document.addEventListener('mouseup', this.stopResize)
      
      // 防止文本选择
      e.preventDefault()
    },

    // 拖拽中
    handleResize(e) {
      if (!this.isResizing) return
      
      const deltaY = e.clientY - this.startY
      const minHeight = 100 // 最小高度
      
      let newRecommendationHeight = this.startRecommendationHeight + deltaY
      
      // 限制最小和最大高度
      newRecommendationHeight = Math.max(minHeight, newRecommendationHeight)
      newRecommendationHeight = Math.min(this.containerHeight - minHeight, newRecommendationHeight)
      
      this.recommendationHeight = newRecommendationHeight
      this.categoryHeight = this.containerHeight - newRecommendationHeight
    },

    // 停止拖拽
    stopResize() {
      this.isResizing = false
      
      // 移除全局事件监听
      document.removeEventListener('mousemove', this.handleResize)
      document.removeEventListener('mouseup', this.stopResize)
    },

  }
}
</script>

<style lang="scss" scoped>
@import './TodayRecommendation.scss';

// 确保样式生效
.item-text {
  font-size: 13px !important;
  color: #2f2f2f !important;
}

.item-separator {
  font-size: 13px !important;
  color: #656565 !important;
}
</style>

